import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useChat } from 'features/Chat'
import { useChatPopup } from 'features/Chat/ChatPopupContext'
import Composer from 'features/Chat/popup/Composer'

/**
 * The composer for the room chats (project, portfolio, location).
 *
 * Renders the same `Composer` the global chat popup uses, so the bottom line is
 * identical in both: `+` for media, a rounded pill input with the emoji button
 * inside it, and a circular send arrow that greys out until there is something
 * to send. It previously rendered `ChatInput`, whose buttons sat outside a
 * square bordered box.
 */
const ChatInputContainer = ({ shouldAlwaysDisplay = false }: { shouldAlwaysDisplay?: boolean }) => {
	const {
		state: {
			chatInput,
			pendingAttachments,
			isSendingMessage,
			startNewChat,
			currentReceiverIds,
			shouldDisplayChatInput,
		},
		actions: {
			handleChatInput,
			handleInsertEmoji,
			handleAddAttachments,
			handleRemoveAttachment,
			handleSendChatMessage,
		},
	} = useChat()

	// Location and project chats render under /locations/:id[/projects/:projectId],
	// so the route already carries the context needed to file uploads in the
	// right image library.
	const { id: routeLocationId, projectId: routeProjectId } = useParams()

	/*
		A popup opened FOR a room carries its own ids and wins.

		The field app's project route is /field/project/:projectId, which these
		params cannot read, so without this an attachment sent from a field
		project chat would have no location to be filed against and would be
		dropped from the image library.
	*/
	const { room } = useChatPopup()
	const locationId = room?.locationId || routeLocationId
	const projectId = room?.projectId || routeProjectId

	const onAddAttachments = useCallback(
		(files: File[]) => handleAddAttachments(files, { locationId, projectId }),
		[handleAddAttachments, locationId, projectId],
	)

	const isDisabled = isSendingMessage || (startNewChat && currentReceiverIds.length === 0)

	if (!shouldAlwaysDisplay && !shouldDisplayChatInput) return null

	return (
		<Composer
			isDisabled={isDisabled}
			value={chatInput}
			pendingAttachments={pendingAttachments}
			onChange={handleChatInput}
			onInsertEmoji={handleInsertEmoji}
			onAddAttachments={onAddAttachments}
			onRemoveAttachment={handleRemoveAttachment}
			onSend={handleSendChatMessage}
		/>
	)
}

export default ChatInputContainer
